import { TEndpoint, TEntity, TLanguage, TReportType, TUserType } from 'contexts/app-context/types'
import { entityPayloadMap } from 'contexts/app-context/types/app-context/components/utils'
import { REPORT_DICTIONARY } from 'common/types/br/Response.types'
import { IRequestTermsRequest } from 'pages/privacy-policy/types/useRequestTerms.types'

interface BridgePrefetchRequestDescriptorInput {
  endpoint?: TEndpoint
  body?: unknown
  headers?: Record<string, unknown>
  endpointUrl?: string
}

const buildOrganizationHeaders = (userCountry: string, userSelectedEntity: TEntity) => ({
  'x-inter-user-org': userCountry,
  'x-inter-account-org': entityPayloadMap[userSelectedEntity],
})

const buildBridgePrefetchRequestDescriptor = ({
  endpoint,
  body,
  headers,
  endpointUrl,
}: BridgePrefetchRequestDescriptorInput) => {
  const method = endpoint?.method ?? 'GET'

  return {
    evtId: endpoint?.id,
    endpoint: endpointUrl ?? endpoint?.url ?? '',
    method,
    body: method.toUpperCase() === 'GET' ? undefined : body,
    headers: {
      ...endpoint?.headers,
      ...headers,
    },
  }
}

const buildOrganizationPrefetchRequestDescriptor = (
  endpoint: TEndpoint | undefined,
  userCountry: string,
  userSelectedEntity: TEntity,
) =>
  buildBridgePrefetchRequestDescriptor({
    endpoint,
    headers: buildOrganizationHeaders(userCountry, userSelectedEntity),
  })

const buildPurposesPrefetchRequestDescriptor = buildOrganizationPrefetchRequestDescriptor

const buildPurposesUsPrefetchRequestDescriptor = (
  endpoint: TEndpoint | undefined,
  userCountry: string,
  userSelectedEntity: TEntity,
  userAccountType: TUserType,
  productId: string,
) =>
  buildBridgePrefetchRequestDescriptor({
    endpoint,
    endpointUrl: endpoint
      ? `app-pf${endpoint.url}${userCountry}/${userAccountType}/${userSelectedEntity}/${productId}`
      : undefined,
    headers: buildOrganizationHeaders(userCountry, userSelectedEntity),
  })

const buildReportStatusPrefetchRequestDescriptor = (
  endpoint: TEndpoint | undefined,
  reportType: TReportType,
  userCountry: string,
  userSelectedEntity: TEntity,
) =>
  buildBridgePrefetchRequestDescriptor({
    endpoint,
    headers: {
      ...buildOrganizationHeaders(userCountry, userSelectedEntity),
      'x-inter-tipo-solicitacao': REPORT_DICTIONARY[reportType],
    },
  })

const buildReportsPrefetchRequestDescriptor = buildOrganizationPrefetchRequestDescriptor

const buildRequestTermsPrefetchRequestDescriptor = (
  endpoint: TEndpoint | undefined,
  requestBody: IRequestTermsRequest,
) =>
  buildBridgePrefetchRequestDescriptor({
    endpoint,
    body: requestBody,
  })

const buildServicesPrefetchRequestDescriptor = buildOrganizationPrefetchRequestDescriptor

const buildFaqSearchPrefetchRequestDescriptor = (
  queryText: string,
  language: TLanguage,
  userSelectedEntity: TEntity,
) => ({
  endpoint: '/api/babi/faq/search',
  method: 'GET',
  body: {
    queryText,
    language,
    userSelectedEntity,
  },
})

const buildFaqAnswerPrefetchRequestDescriptor = (
  id: string,
  language: TLanguage,
  userSelectedEntity: TEntity,
) => ({
  endpoint: '/api/babi/faq/answer',
  method: 'GET',
  body: {
    id,
    language,
    userSelectedEntity,
  },
})

export type { BridgePrefetchRequestDescriptorInput }
export {
  buildBridgePrefetchRequestDescriptor,
  buildFaqAnswerPrefetchRequestDescriptor,
  buildFaqSearchPrefetchRequestDescriptor,
  buildOrganizationHeaders,
  buildPurposesPrefetchRequestDescriptor,
  buildPurposesUsPrefetchRequestDescriptor,
  buildReportStatusPrefetchRequestDescriptor,
  buildReportsPrefetchRequestDescriptor,
  buildRequestTermsPrefetchRequestDescriptor,
  buildServicesPrefetchRequestDescriptor,
}
